import TSP_PERMISSIONS from "../constants/tspPermissions";

// ---------------------------------------------------------------------------
// PRIMARY NAVIGATION — the approved header's tabs, in order (Bus Leaders Directory temporarily hidden).
//
// This is application navigation, NOT an access-control list. It is
// deliberately never filtered by permissions (nor by whether GET /api/tsp/me
// has resolved or failed): the header must look the same for every signed-in
// user. Access is enforced where it belongs — per route by TspProtectedRoute
// (App.js), per action by useTspPermission().hasPermission, and on the server
// by requireTspPermission. Clicking a tab the user cannot use lands on that
// route's "no access" state; the tab itself never disappears.
//
// Do not add a `permission` field or a filter to these items. If a product
// decision later requires hiding a top-level tab by permission, make that an
// explicit, separate change.
//
// `isActive(pathname)` decides which tab is underlined. It is explicit (not a
// prefix match on `route`) because the designs highlight a tab for whole flows:
// the journey editor and the Add Book Distribution screen sit under "Add
// Journey", and journey stats sit under "Your Stats".
//
// No `icon` field: the approved header design shows text-only tabs.
// ---------------------------------------------------------------------------
export const PRIMARY_NAV_ITEMS = Object.freeze([
  { key: "dashboard", label: "Dashboard", route: "/tsp", isActive: (p) => p === "/tsp" },
  { key: "journeys", label: "Journey", route: "/tsp/journeys", isActive: (p) => p === "/tsp/journeys" },
  { key: "journey-list", label: "Journey List", route: "/tsp/journey-list", isActive: (p) => p === "/tsp/journey-list" },
  {
    key: "add-journey",
    label: "Add Journey",
    route: "/tsp/journeys/new",
    isActive: (p) => p.startsWith("/tsp/journeys/new") || p.startsWith("/tsp/book-distribution/new"),
  },
  // Temporarily hidden from the header (product request). The /tsp/teams route
  // still exists; restore this line to bring the tab back.
  // { key: "bus-leaders-directory", label: "Bus Leaders Directory", route: "/tsp/teams", isActive: (p) => p.startsWith("/tsp/teams") },
  { key: "your-stats", label: "Your Stats", route: "/tsp/stats", isActive: (p) => p.startsWith("/tsp/stats") },
  { key: "live-ranking", label: "Live Ranking", route: "/tsp/live-ranking", isActive: (p) => p.startsWith("/tsp/live-ranking") },
  { key: "live-tracking", label: "Live Tracking", route: "/tsp/live-tracking", isActive: (p) => p.startsWith("/tsp/live-tracking") },
  { key: "success-stories", label: "Success Stories", route: "/tsp/success-stories", isActive: (p) => p.startsWith("/tsp/success-stories") },
]);

// ---------------------------------------------------------------------------
// SECONDARY MODULES — everything that is NOT one of the primary tabs is
// reachable from the "TSP modules" section of the profile menu, grouped by
// `group` (Operations / Knowledge / Reporting). Entries that are already
// primary tabs carry no group and are never repeated there. Unlike the primary
// nav, this menu IS filtered by permission.
//
// Mirrors the { name, link } shape header.jsx already uses for its menu array,
// plus a `permission` key.
// ---------------------------------------------------------------------------
const tspMenu = [
  { name: "Dashboard", link: "/tsp", permission: TSP_PERMISSIONS.DASHBOARD_VIEW },
  { name: "Teams", link: "/tsp/teams", permission: TSP_PERMISSIONS.TEAMS_VIEW }, // primary tab: Bus Leaders Directory
  { name: "Journeys", link: "/tsp/journeys", permission: TSP_PERMISSIONS.JOURNEYS_VIEW },
  { name: "Route Planning", link: "/tsp/route-planning", permission: TSP_PERMISSIONS.ROUTE_PLANNING_VIEW, group: "Operations" },
  { name: "City Visits", link: "/tsp/city-visits", permission: TSP_PERMISSIONS.CITY_VISITS_VIEW, group: "Operations" },
  { name: "Locations", link: "/tsp/locations", permission: TSP_PERMISSIONS.LOCATIONS_VIEW, group: "Operations" },
  { name: "Contacts", link: "/tsp/contacts", permission: TSP_PERMISSIONS.CONTACTS_VIEW, group: "Operations" },
  { name: "Accommodation", link: "/tsp/accommodation", permission: TSP_PERMISSIONS.ACCOMMODATION_VIEW, group: "Operations" },
  { name: "Prasadam", link: "/tsp/prasadam", permission: TSP_PERMISSIONS.PRASADAM_VIEW, group: "Operations" },
  { name: "Daily Reports", link: "/tsp/daily-reports", permission: TSP_PERMISSIONS.DAILY_REPORTS_VIEW, group: "Operations" },
  { name: "Book Distribution", link: "/tsp/book-distribution", permission: TSP_PERMISSIONS.BOOK_DISTRIBUTION_VIEW, group: "Operations" },
  { name: "Public Response", link: "/tsp/public-response", permission: TSP_PERMISSIONS.PUBLIC_RESPONSE_VIEW, group: "Operations" },
  { name: "Advice Repository", link: "/tsp/advice", permission: TSP_PERMISSIONS.ADVICE_VIEW, group: "Knowledge" },
  { name: "Stories", link: "/tsp/success-stories", permission: TSP_PERMISSIONS.STORIES_VIEW },
  { name: "Reports", link: "/tsp/reports", permission: TSP_PERMISSIONS.REPORTS_VIEW, group: "Reporting" },
  { name: "Notifications", link: "/tsp/notifications", permission: TSP_PERMISSIONS.NOTIFICATIONS_VIEW, group: "Reporting" },
  { name: "Search", link: "/tsp/search", permission: null, group: "Reporting" },
];

export const TSP_MENU_GROUPS = ["Operations", "Knowledge", "Reporting"];

// `hasPermission` is injected (rather than calling useTspPermission here)
// so this stays a plain, testable function — see tspMenu.test.js.
export function getVisibleTspMenu(hasPermission) {
  return tspMenu.filter((item) => item.permission === null || hasPermission(item.permission));
}

// The profile menu's "TSP modules" section: visible, grouped entries only.
export function getGroupedModules(hasPermission) {
  const visible = getVisibleTspMenu(hasPermission).filter((item) => item.group);
  return TSP_MENU_GROUPS.map((name) => ({ name, items: visible.filter((item) => item.group === name) })).filter((group) => group.items.length > 0);
}

export default tspMenu;
